import { store } from '@/redux/store'

import { W3ResponseType } from '../events'
import { w3Api } from '../w3Api'
import { feedType, feedTypeCard, fullSearchType, PostCommentType, PutFeedLikeParams, TypeSharePost } from './feed.type'

export const FeedEndpoints = {
  getFeed: '/post',
  sharePost: '/post/:postId/share',
  comment: '/comment',
  putFeedLike: '/post/:postId/like',
  fullSearch: '/base/search',
}

export const feedWeb3Api = w3Api.injectEndpoints({
  endpoints: (build) => ({
    getFeed: build.query<W3ResponseType<feedTypeCard[]>, feedType>({
      query: (args) => {
        return {
          url: FeedEndpoints.getFeed,
          params: args,
          // method: 'GET',
        }
      },
      providesTags: (result, err, arg): any =>
        result
          ? [
              ...(Array.isArray(result.data)
                ? result.data.map(({ id }: { id: string }) => ({ type: 'Feed' as const, id: id }))
                : []),
              { type: 'Feed', searchType: arg.searchType },
              { type: 'Feed' },
            ]
          : [{ type: 'Feed', searchType: arg.searchType }, { type: 'Feed' }],
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        return `${endpointName}-${queryArgs.searchType}`
      },
      merge: (currentCache, newItems) => {
        if (newItems.meta.page === 1) {
          return newItems
        }
        return {
          ...currentCache,
          data: [...currentCache.data, ...newItems.data],
          meta: {
            hasNext: newItems.meta.hasNext,
            hasPrev: newItems.meta.hasPrev,
            limit: newItems.meta.limit,
            page: newItems.meta.page,
            totalItem: newItems.meta.totalItem,
            totalPage: newItems.meta.totalPage,
          },
        }
      },
      forceRefetch({ currentArg, previousArg }) {
        return currentArg !== previousArg
      },
    }),
    fullSearch: build.query<W3ResponseType<any[]>, fullSearchType>({
      query: (args) => {
        return {
          url: FeedEndpoints.fullSearch,
          params: args,
          // method: 'GET',
        }
      },
      providesTags: (result, err, arg): any =>
        result
          ? [
              ...(Array.isArray(result.data)
                ? result.data.map(({ id }: { id: string }) => ({ type: 'Search' as const, id: id }))
                : []),
              { type: 'Search', keySearch: arg.keySearch },
              { type: 'Search' },
            ]
          : [{ type: 'Search', keySearch: arg.keySearch }, { type: 'Search' }],
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        return `${endpointName}-${queryArgs.keySearch}-${queryArgs.type}`
      },
      merge: (currentCache, newItems) => {
        if (newItems.meta.page === 1) {
          return newItems
        }
        return {
          ...currentCache,
          data: [...currentCache.data, ...newItems.data],
          meta: {
            hasNext: newItems.meta.hasNext,
            hasPrev: newItems.meta.hasPrev,
            limit: newItems.meta.limit,
            page: newItems.meta.page,
            totalItem: newItems.meta.totalItem,
            totalPage: newItems.meta.totalPage,
          },
        }
      },
      forceRefetch({ currentArg, previousArg }) {
        return currentArg?.page !== previousArg?.page
      },
    }),
    sharePost: build.mutation<any, TypeSharePost>({
      query: (args) => {
        return {
          url: FeedEndpoints.sharePost.replace(':postId', args.postId),
          method: 'POST',
          body: {
            content: args.content,
            isPublic: args.isPublic,
            spaceId: args.spaceId,
          },
        }
      },
      async onQueryStarted(args, { dispatch, queryFulfilled }) {
        try {
          const { data: createdPost } = await queryFulfilled
          const state = store.getState()
          const creator = state.root.auth.user
          const orginPost = createdPost.originalPost || {}

          if (createdPost) {
            const newData = {
              ...createdPost,
              creatorId: args.spaceId || createdPost.creator?.id,
              postAs: args.spaceId ? 'SPACE' : 'USER',
              avatarImage: creator?.avatarImage || '',
              priceBuyKey: creator?.priceBuyKey || 0,
              nickname: creator?.nickname || '',
              originalPost: {
                postId: orginPost.id,
                content: orginPost.content,
                imageUrls: orginPost.imageUrls,
                creatorId: orginPost?.space?.id || orginPost.creator?.id,
                nickname: orginPost?.space?.name || orginPost.creator?.nickname,
                avatarImage: orginPost?.space?.profileImageUrl || orginPost.creator?.avatarImage,
              },
            }
            // newfeed
            dispatch(
              feedWeb3Api.util.updateQueryData(
                'getFeed',
                { page: 1, size: 6, searchType: 'all', sort: 'DESC' },
                (draft) => {
                  if (draft.data) {
                    draft.data.unshift(newData)
                  }
                },
              ),
            )

            // profile
            dispatch(
              feedWeb3Api.util.updateQueryData(
                'getFeed',
                { page: 1, size: 6, searchType: 'my_post', sort: 'DESC' },
                (draft) => {
                  if (draft.data) {
                    draft.data.unshift(newData)
                  }
                },
              ),
            )
          }
        } catch (error) {
          console.error('Failed to update cache:', error)
        }
      },
    }),
    putFeedLike: build.mutation<any, PutFeedLikeParams>({
      query: (args) => {
        return {
          url: FeedEndpoints.putFeedLike.replace(':postId', args.postId),
          method: 'POST',
        }
      },
    }),

    getDetailFeed: build.query<feedTypeCard, { id: string }>({
      query: (args) => {
        return {
          url: `${FeedEndpoints.getFeed}/${args.id}`,
        }
      },
      providesTags(result, error, arg) {
        return [{ type: 'Feed', id: arg.id }]
      },
    }),
    // comment
    getComment: build.query<W3ResponseType<any>, { postId: string; size: number; page: number }>({
      query: (args) => {
        return {
          url: FeedEndpoints.comment,
          params: { limit: args.size, page: args.page, postId: args.postId, sort: 'DESC' },
        }
      },
      providesTags: (result, error, arg) =>
        result
          ? [
              // eslint-disable-next-line no-unsafe-optional-chaining
              ...result?.data?.map(({ id }: { id: string }) => ({ type: 'Comment' as const, id })),
              { type: 'Comment', postId: arg.postId },
            ]
          : [{ type: 'Comment', postId: arg.postId }],

      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        return `${endpointName}-${queryArgs.postId}`
      },
      merge: (currentCache, newItems) => {
        if (newItems.meta.page === 0) {
          return newItems
        }
        return {
          ...currentCache,
          data: [...currentCache.data, ...newItems.data],
          meta: {
            hasNext: newItems.meta.hasNext,
            hasPrev: newItems.meta.hasPrev,
            limit: newItems.meta.limit,
            page: newItems.meta.page,
            totalItem: newItems.meta.totalItem,
            totalPage: newItems.meta.totalPage,
          },
        }
      },
      forceRefetch({ currentArg, previousArg }) {
        return currentArg !== previousArg
      },
    }),

    comment: build.mutation<any, PostCommentType>({
      query: (args) => {
        const data = {
          content: args.content,
          postId: args.postId,
          commentParentId: args.commentParentId,
          imageUrl: args.imageUrl,
        } as PostCommentType
        if (args.spaceId) {
          data.spaceId = args.spaceId
        }
        return {
          url: FeedEndpoints.comment,
          method: 'POST',
          body: data,
        }
      },
      async onQueryStarted(args, { dispatch, queryFulfilled }) {
        try {
          const state = store.getState()
          const creator = state.root.auth.user
          const { data: createdComment } = await queryFulfilled
          if (createdComment) {
            const data = {
              ...createdComment,
              creator: {
                ...createdComment.creator,
                nickname: creator?.nickname,
                avatarImage: creator?.avatarImage,
                id: createdComment?.space?.id,
                commentAs: 'SPACE',
              },
            }
            dispatch(
              feedWeb3Api.util.updateQueryData('getComment', { page: 0, size: 10, postId: args.postId }, (draft) => {
                if (draft.data) {
                  draft.data.unshift(data)
                }
              }),
            )
          }
        } catch (error) {
          console.error('Failed to update cache:', error)
        }
      },
      invalidatesTags: (result, error, arg) => [{ type: 'Feed', id: arg.postId }],
    }),
  }),
  overrideExisting: true,
})

export const {
  useGetFeedQuery,
  useFullSearchQuery,
  useLazyGetFeedQuery,
  useSharePostMutation,
  useGetCommentQuery,
  useLazyGetCommentQuery,
  useCommentMutation,
  usePutFeedLikeMutation,
  useGetDetailFeedQuery,
  useLazyGetDetailFeedQuery,
} = feedWeb3Api
